import CallToActionOutlinedIcon from '@mui/icons-material/CallToActionOutlined';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import VoiceOverOffIcon from '@mui/icons-material/VoiceOverOff';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Button, Tab, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import { Duration } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { trackModalUpdates, trackSaveCadence } from 'src/common/userTracking';
import * as Yup from 'yup';
import { SettingsIcon } from '../../../images/CadenceIcons';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import { Cadence } from '../../../lib/kixie-entities/tables/Cadence';
import { endingDate } from '../../../recoil/atoms/endingDate';
import { startingDate } from '../../../recoil/atoms/startingDate';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { CadenceActions } from '../CadenceSettings/CadenceActions';
import { Dispositions } from '../CadenceSettings/Dispositions';
import { HarassmentRules } from '../CadenceSettings/HarassmentRules';
import { Settings } from '../CadenceSettings/Settings';
import TabLabel from '../general/TabLabel';
import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import { WizardModal } from './WizardModal';

export function tConvert(time: any) {
  //converts 24 hour time to 12 hour with am pm
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];
  if (time.length > 1) {
    // If time format correct
    time = time.slice(1); // Remove full string match value
    time[5] = +time[0] < 12 ? ' AM' : ' PM'; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
  }
  return time.join(''); // return adjusted time or original string
}

function convertMinutes(minutes: any) {
  minutes = Number(minutes);
  const DaysHoursMinutes = {
    days: Math.floor(minutes / (60 * 24)),
    hours: Math.floor((minutes % (60 * 24)) / 60),
    minutes: Math.floor(minutes % 60),
  };

  return DaysHoursMinutes;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogActions: {
    padding: '0px',
    display: 'initial',
    height: '40px',
    borderTop:
      theme.palette.mode === 'dark' ? '1px solid #737373' : '1px solid #77849D',
  },
  dialogPaper: {
    minHeight: '80vh',
    position: 'absolute',
    top: '1%',
  },
  warningIcon: {
    color: 'orange',
  },

  button: {
    marginLeft: theme.spacing(1), // space between fields
  },

  dialogContent: {
    paddingTop: '10px',
  },
  title: {
    paddingLeft: '15px',
    paddingTop: '18px',
    paddingRight: '5px',
    paddingBottom: '7px',
    fontFamily: 'serif',
  },
  tabs: {},
  text: {
    color: theme.palette.mode === 'dark' ? 'black' : 'white',
    paddingLeft: '30px',
  },
  background: {
    // backgroundColor: '#',
  },
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabIcon: {
    marginRight: 15,
  },
  reset: {
    margin: 0,
    padding: 0,
  },
}));

export const isRerouteAction = (action) => action.type === 'reroute';

export function CadenceModal(props: {
  show: boolean;
  handleClose: () => void;
  enqueueSnackbarWithStyles: any;
  id: number | null;
}) {
  const classes = useStyles();
  const start = useRecoilValueLoadable<any>(startingDate);
  const end = useRecoilValueLoadable<any>(endingDate);
  const tabNames = [
    {
      title: 'Settings',
      icon: SettingsApplicationsIcon,
    },
    {
      title: 'Actions',
      icon: CallToActionOutlinedIcon,
    },
    {
      title: 'Call Outcomes',
      icon: SettingsIcon,
    },
    {
      title: 'Harassment Rules',
      icon: VoiceOverOffIcon,
    },
  ];
  const [tabIndex, setTabIndex] = useState(0);
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);
  const handleChange = (formik: FormikProps<any>) => async (
    event: any,
    newIndex: number
  ) => {
    setTabIndex(+newIndex);
  };
  const [userLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, any>
  >(user);
  const userAtom = userLoadable.contents as IUserAtom;

  const setRefetchForCadenceReport = useSetRecoilState(
    refetchToggle(
      `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtom?.businessid}`
    )
  );
  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const tabToFields = [
    ['cadenceName', 'queueId', 'cadenceHours', 'targetHours'],
    ['cadenceActions'],
    ['cadenceDispositionRules'],
    ['cadenceHarassmentPolicies'],
  ];
  const data = {
    call: methodNameEnum.GET_SMS_TEMPLATE,
  };
  const smsTemplates = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_SMS_TEMPLATE,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const smsTemplateOptions = useMemo(
    () =>
      smsTemplates.state === 'hasValue'
        ? [...(smsTemplates.contents?.response?.data?.result ?? [])]
            ?.filter((template) => template.hasCustomField === 0)
            .sort((a, b) =>
              a.smsTemplateName
                ?.toLocaleLowerCase()
                ?.localeCompare(b?.smsTemplateName?.toLocaleLowerCase())
            )
        : [],
    [smsTemplates]
  );

  const [
    cadenceCached,
    cadenceLoadable,
    setRefetchForCadence,
  ] = makeApiRequestCached<Cadence | undefined>(
    {
      endpoint: `${endpointEnum.GET_CADENCE}/${props.id}?apikey=${userAtom?.apikey}`,
      method: 'get',
      waitForPayload: !props.id,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business & {
      // eslint-disable-next-line func-call-spacing
      businessHarassmentPolicies: (BusinessHarassmentPolicy & {
        days: number;
        hours: number;
        minutes: number;
      })[];
    }
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom?.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business: Business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const disposition = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: start.contents,
        enddate: end.contents,
        timezone: businessCached.current?.timezone?.timezonePhp,
      }),
      waitForPayload: !businessCached.current?.timezone?.timezonePhp,
    })
  );

  const dispoList = useMemo(
    () =>
      disposition.state === 'hasValue'
        ? disposition.contents?.data?.dispositions?.result
        : [],
    [disposition]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        cadenceName: Yup.string().required('Required'),
        queueId: Yup.string().required('Required'),
        smsInboxId: Yup.string()
          .ensure()
          .when('smsSender', {
            is: (val) => val !== 'useAgent',
            then: Yup.string().required('Required'),
          }),
        smsBusinessNumber: Yup.string()
          .ensure()
          .when('smsInboxId', {
            is: (val) => !!val,
            then: Yup.string().required('Required'),
          }),
        cadenceActions: Yup.array()
          .of(
            Yup.object().shape({
              days: Yup.number()
                .required('required')
                .min(0, 'Minimum 0')
                .max(90, 'Max 90'),

              hours: Yup.number()
                .required('required')
                .min(0, 'Minimum 0')
                .max(23, 'Max 23'),
              minutes: Yup.number()
                .required('required')
                .min(0, 'Minimum 0')
                .max(59, 'Max 59'),
              type: Yup.string().required('required'),
              smsMessage: Yup.string().when('type', {
                is: (type) => type?.toLocaleLowerCase() === 'sms',
                then: Yup.string().required('Message is Required.'),
              }),
              durationDays: Yup.number()
                .required('required')
                .min(0, 'Minimum 0')
                .max(90, 'Max 90'),
              durationHours: Yup.number()
                .required('required')
                .min(0, 'Minimum 0')
                .max(23, 'Max 23'),
              durationMinutes: Yup.number()
                .required('required')
                .min(0, 'Minimum 0')
                .max(59, 'Max 59'),
              webhookHeaders: Yup.array().of(
                Yup.object().shape(
                  {
                    name: Yup.string()
                      .transform((curr, orig) => (orig === '' ? null : curr))
                      .when(['value'], {
                        is: (value, name) => !name && value,
                        then: Yup.string()
                          .required('Name is required')
                          .matches(
                            /^[a-zA-Z0-9_-]+$/,
                            'Name format is incorrect'
                          ),
                      }),
                    value: Yup.string()
                      .transform((curr, orig) => (orig === '' ? null : curr))
                      .when(['name'], {
                        is: (name, value) => !value && name,
                        then: Yup.string()
                          .required('Value is required')
                          .matches(
                            //eslint-disable-next-line
                            /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/,
                            'Value format is incorrect'
                          ),
                      }),
                  },
                  [['value', 'name']]
                )
              ),
              webhookUrl: Yup.string().when('type', {
                is: (type) => type?.toLocaleLowerCase() === 'webhook',
                then: Yup.string()
                  .required('Required')
                  .url('must be a valid URL'),
              }),
            })
          )
          .test({
            name: 'reroute-action-last',
            message: 'Reroute action must be last',
            test: (val: any) => {
              const total = val.filter((a) => a.status === 'active').length;
              return val.every((action, index) => {
                if (action.status === 'deleted') {
                  return true;
                }
                const isLast = index == total - 1;
                if (!isLast && isRerouteAction(action)) {
                  return false;
                }
                return true;
              });
            },
          })
          .test({
            name: 'only-one-reroute-action',
            message: 'Only one reroute action allowed',
            test: (val: any) => {
              return (
                val.filter((action) => isRerouteAction(action)).length <= 1
              );
            },
          }),
        cadenceDispositionRules: Yup.array().of(
          Yup.object().shape(
            {
              action: Yup.string().when('businessDispositionId', {
                is: (val) => !!val,
                then: Yup.string().required('Selection is required'),
                otherwise: Yup.string(),
              }),
              businessDispositionId: Yup.number().when('action', {
                is: (val) => !!val,
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              forwardToCadenceId: Yup.string().when('action', {
                is: (action) => action === 'forward',
                then: Yup.string().required('Required'),
              }),
              forwardToPowerlistId: Yup.string().when('action', {
                is: (action) => action === 'forwardToPowerlist',
                then: Yup.string().required('Required'),
              }),
            },
            [['action', 'businessDispositionId']]
          )
        ),
        cadenceHarassmentPolicies: Yup.array().of(
          Yup.object().shape({
            frequency: Yup.number()
              .required('required')
              .min(0, 'Minimum 0')
              .max(9999, 'Max 9999'),
            days: Yup.number()
              .required('required')
              .min(0, 'Minimum 0')
              .max(90, 'Max 90'),
            hours: Yup.number()
              .required('required')
              .min(0, 'Minimum 0')
              .max(23, 'Max 23'),
            minutes: Yup.number()
              .required('required')
              .min(0, 'Minimum 0')
              .max(59, 'Max 59'),
          })
        ),
      }),
    []
  );
  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable]
  );
  const addNewCadenceInitialValues = new Cadence({
    cadenceName: '',
    eventUrl: '',
    queueId: '',
    reEnrollmentEnabled: false,
    useContactOwnerEnabled: false,
    checkDncEnabled: false,
    useBusinessCadenceHoursEnabled: true,
    useBusinessTargetHoursEnabled: true,
    assignContactOwner: 'autoAssignFromQueue',
    cadenceActions: [
      {
        type: 'call',
        days: 0,
        hours: 0,
        minutes: 0,
        durationDays: 0,
        durationHours: 0,
        durationMinutes: 0,
        retryCallEnabled: false,
        maxRetries: 1,
        smsTemplateId: '',
        smsMessage: '',
        custom: false,
        status: 'active',
        webhookUrl: '',
        webhookHeaders: [{ name: '', value: '' }],
      } as any,
    ],
    cadenceHours: businessHoursMemo,
    targetHours: businessHoursMemo,
    cadenceDispositionRules: [
      {
        businessDispositionId: '',
        action: '',
        forwardToCadenceId: '',
        forwardToPowerlistId: '',
      } as any,
    ],
    cadenceHarassmentPolicies: [
      {
        channel: 'call',
        frequency: 3,
        durationMin: 1440,
        days: 1,
        hours: 0,
        minutes: 0,
      } as any,
      {
        channel: 'sms',
        frequency: 2,
        durationMin: 1440,
        days: 1,
        hours: 0,
        minutes: 0,
      } as any,
    ],
    smsSender: 'useAgent',
    smsInboxId: null,
    smsBusinessNumber: null,
    useBusinessHarassmentPolicyEnabled: true,
    businessHarassmentPolicies:
      businessCached.current?.businessHarassmentPolicies,
  } as any);

  const editCadenceInitialValuesMemo = useMemo(
    () =>
      cadenceLoadable.state === 'hasValue'
        ? new Cadence({
            cadenceId: Number(props.id),
            // to do:  input initial values
            eventUrl: decodeURI(cadenceCached.current?.eventUrl ?? ''),
            cadenceName: cadenceCached.current?.cadenceName ?? '',
            queueId: cadenceCached.current?.queueId ?? '',
            // from here to do
            reEnrollmentEnabled:
              cadenceCached.current?.reEnrollmentEnabled ?? false,
            useContactOwnerEnabled:
              cadenceCached.current?.useContactOwnerEnabled ?? false,
            useBusinessCadenceHoursEnabled:
              cadenceCached.current?.useBusinessCadenceHoursEnabled ?? false,
            useBusinessTargetHoursEnabled:
              cadenceCached.current?.useBusinessTargetHoursEnabled ?? false,
            checkDncEnabled: cadenceCached.current?.checkDncEnabled ?? false,
            assignContactOwner:
              cadenceCached.current?.assignContactOwner ??
              'autoAssignFromQueue',
            cadenceActions:
              cadenceCached.current?.cadenceActions?.map(
                (ca) =>
                  ({
                    cadenceActionId: ca?.cadenceActionId,
                    cadenceId: Number(props.id),
                    type: ca?.type?.startsWith('reroute')
                      ? 'reroute'
                      : ca?.type,
                    rerouteType: ca?.type?.startsWith('reroute')
                      ? ca?.type
                      : '',
                    rerouteToCadenceId: ca?.rerouteToCadenceId,
                    rerouteToPowerlistId: ca?.rerouteToPowerlistId,
                    days: convertMinutes(ca?.durationFromLastActionMin).days,
                    hours: convertMinutes(ca?.durationFromLastActionMin).hours,
                    minutes: convertMinutes(ca?.durationFromLastActionMin)
                      .minutes,
                    retryCallEnabled: ca?.retryCallEnabled ?? false ? 1 : 0,
                    maxRetries: ca?.maxRetries?.toString() ?? '1',
                    durationDays: convertMinutes(ca?.callRetryInMin).days,
                    durationHours: convertMinutes(ca?.callRetryInMin).hours,
                    durationMinutes: convertMinutes(ca?.callRetryInMin).minutes,
                    smsTemplateId: ca?.smsTemplateId ?? '',
                    smsMessage: ca?.smsMessage
                      ? ca?.smsMessage
                      : smsTemplateOptions.find(
                          (sms) =>
                            String(sms.smsTemplateid) ===
                            String(ca?.smsTemplateId)
                        )?.smsTemplateMessage ?? '',
                    custom: false,
                    status: ca.status,
                    webhookUrl: ca?.webhookUrl ?? '',
                    webhookHeaders: ca?.webhookHeaders
                      ? JSON.parse(ca?.webhookHeaders ?? '')
                      : [{ name: '', value: '' }],
                  } as any)
              ) ??
              ([
                {
                  // safety catch
                  type: '',
                  days: 0,
                  hours: 0,
                  minutes: 0,
                  retryCallEnabled: 0,
                  maxRetries: '1',
                  durationDays: 0,
                  durationHours: 0,
                  durationMinutes: 0,
                  smsTemplateId: '',
                  smsMessage: '',
                  custom: false,
                },
              ] as any),
            //cadence hours from db is array of 7 objects with a key of hours which is an array of start/end times
            cadenceHours:
              cadenceCached.current?.cadenceHours ?? businessHoursMemo,
            targetHours:
              cadenceCached.current?.targetHours ?? businessHoursMemo,
            cadenceDispositionRules: cadenceCached.current
              ?.cadenceDispositionRules?.length
              ? (cadenceCached.current?.cadenceDispositionRules?.map((dr) => ({
                  businessDispositionId: dr?.businessDispositionId ?? '',
                  action: dr?.action ?? '',
                  forwardToCadenceId: dr.forwardToCadenceId ?? '',
                  forwardToPowerlistId: dr.forwardToPowerlistId ?? '',
                  cadenceId: Number(props.id),
                  cadenceDispositionRuleId: dr?.cadenceDispositionRuleId ?? '',
                })) as any)
              : ([
                  {
                    businessDispositionId: '',
                    action: '',
                    forwardToCadenceId: '',
                    forwardToPowerlistId: '',
                  },
                ] as any),

            cadenceHarassmentPolicies: !!cadenceCached.current
              ?.cadenceHarassmentPolicies?.length
              ? cadenceCached.current?.cadenceHarassmentPolicies?.map((ha) => ({
                  ...ha,
                  days: Number(convertMinutes(ha?.durationMin).days),
                  hours: Number(convertMinutes(ha?.durationMin).hours),
                  minutes: Number(convertMinutes(ha?.durationMin).minutes),
                }))
              : addNewCadenceInitialValues.cadenceHarassmentPolicies,
            useBusinessHarassmentPolicyEnabled:
              cadenceCached.current?.useBusinessHarassmentPolicyEnabled,
            businessHarassmentPolicies:
              businessCached.current?.businessHarassmentPolicies,
            // review: {},
            smsSender: cadenceCached.current?.smsSender ?? 'useAgent',
            smsInboxId: cadenceCached.current?.smsInboxId,
            smsBusinessNumber: cadenceCached.current?.smsBusinessNumber,
          } as any)
        : addNewCadenceInitialValues,
    [cadenceLoadable, smsTemplateOptions, businessLoadable, props.id]
  );
  function trackUpdates(values, editCadenceInitialValuesMemo, currentTabTitle) {
    trackModalUpdates(
      values,
      editCadenceInitialValuesMemo,
      'Cadence Settings Updated',
      currentTabTitle
    );
  }
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        trackUpdates(values, editCadenceInitialValuesMemo, currentTabTitle);
        values.cadenceDispositionRules = values.cadenceDispositionRules?.filter(
          (cadenceDispositionRule) => {
            return !!cadenceDispositionRule?.businessDispositionId;
          }
        );
        const payload = new Cadence({
          cadenceId: values?.cadenceId,
          eventUrl: values.eventUrl,
          businessId: String(userAtom?.businessid),
          status: 'active',
          cadenceName: values.cadenceName,
          checkDncEnabled: values.checkDncEnabled,
          reEnrollmentEnabled: values.reEnrollmentEnabled,
          useContactOwnerEnabled: values.useContactOwnerEnabled,
          useBusinessHarassmentPolicyEnabled:
            values.useBusinessHarassmentPolicyEnabled,
          useBusinessCadenceHoursEnabled: values.useBusinessCadenceHoursEnabled,
          useBusinessTargetHoursEnabled: values.useBusinessTargetHoursEnabled,
          queueId: values.queueId,
          cadenceHours: values.cadenceHours,
          targetHours: values.targetHours,
          assignContactOwner: values.assignContactOwner,
          cadenceActions: values.cadenceActions?.map(
            (cadenceAction: any, index) => {
              const action = {
                cadenceActionId: cadenceAction?.cadenceActionId,
                cadenceId: values?.cadenceId,
                status: cadenceAction.status,
                actionIndex: index,
                type:
                  cadenceAction.type === 'reroute'
                    ? cadenceAction.rerouteType
                    : cadenceAction.type,
                rerouteToCadenceId: cadenceAction.rerouteToCadenceId,
                rerouteToPowerlistId: cadenceAction.rerouteToPowerlistId,
                durationFromLastActionMin:
                  Number(cadenceAction.days) * 1440 +
                  Number(cadenceAction.hours) * 60 +
                  Number(cadenceAction.minutes),
                callRetryInMin:
                  Number(cadenceAction.durationDays) * 1440 +
                  Number(cadenceAction.durationHours) * 60 +
                  Number(cadenceAction.durationMinutes),
                retryCallEnabled: !!cadenceAction.retryCallEnabled,
                maxRetries: Number(cadenceAction.maxRetries),
                smsTemplateId:
                  !cadenceAction.custom && !!cadenceAction.smsTemplateId
                    ? String(cadenceAction.smsTemplateId)
                    : null,
                smsMessage:
                  cadenceAction.custom || !cadenceAction.smsTemplateId
                    ? cadenceAction.smsMessage
                    : null,
                webhookUrl: cadenceAction?.webhookUrl
                  ? cadenceAction.webhookUrl
                  : null,
              } as any;

              if (
                cadenceAction?.webhookHeaders &&
                cadenceAction.webhookHeaders?.[0]?.name !== '' &&
                cadenceAction.webhookHeaders?.[0]?.value !== ''
              ) {
                action.webhookHeaders = cadenceAction.webhookHeaders;
              } else {
                action.webhookHeaders = null;
              }

              return action;
            }
          ),
          smsBusinessNumber: values.smsBusinessNumber,
          smsInboxId: values.smsInboxId,
          smsSender: values.smsSender,
          cadenceDispositionRules: !!values.cadenceDispositionRules?.[0]?.action
            ? values.cadenceDispositionRules.map((dispo, index) => ({
                businessDispositionId: String(dispo.businessDispositionId),
                action: dispo.action,
                forwardToCadenceId:
                  dispo.action == 'stop' ? 0 : Number(dispo.forwardToCadenceId),
                forwardToPowerlistId:
                  dispo.action == 'stop'
                    ? 0
                    : Number(dispo.forwardToPowerlistId),
                cadenceId: values?.cadenceId,
                cadenceDispositionRuleId: dispo?.cadenceDispositionRuleId,
              }))
            : [],
          cadenceHarassmentPolicies: values.cadenceHarassmentPolicies?.map(
            (ha: any) =>
              ({
                ...ha,
                durationMin:
                  Number(ha.days) * 1440 +
                  Number(ha.hours) * 60 +
                  Number(ha.minutes),
              } as any)
          ),
        });
        try {
          await mutation.mutateAsync({
            // placeholder for actual call
            endpoint:
              endpointEnum.ADD_NEW_CADENCE + (props.id ? `/${props.id}` : ''),
            method: props.id ? 'put' : 'post',
            omitBusinessIdAndApikey: true,
            payload,
          });

          props.enqueueSnackbarWithStyles({
            message: `Successfully ${props.id ? 'Edited' : 'Added'} ${
              values.cadenceName
            } Cadence!`,
            alertProps: { severity: 'success' },
          });

          // console.log(status, 'tjhis is the status');
          setRefetchForCadenceReport({});
          setRefetchForCadence({});
          actions.resetForm({});
          props.handleClose();
        } catch (ex: any) {
          let message = 'Encountered an error';
          let severity = 'error';

          if (ex.response.data.code === 'ER_DUP_ENTRY') {
            message = 'Cadence name already taken, please choose another name.';
            severity = 'warning';
          }

          props.enqueueSnackbarWithStyles({
            message,
            alertProps: { severity },
          });
        }
      }}
      initialValues={
        props.id ? editCadenceInitialValuesMemo : addNewCadenceInitialValues
      }
    >
      {(formik) => {
        const filteredDispoList = useMemo(() => {
          return (
            [...(dispoList ?? [])].sort((a, b) =>
              a?.disposition
                ?.toString()
                .toLowerCase()
                ?.localeCompare(b?.disposition?.toString().toLowerCase())
            ) ?? []
          );
        }, [formik.values.cadenceDispositionRules, dispoList]);
        // console.log(formik.values, 'formik vlaues');
        React.useEffect(() => {
          //To do add harrasment policies listener
          for (
            let i = 0;
            i < Number(formik.values.cadenceActions?.length);
            i++
          ) {
            if (
              formik.values.cadenceActions?.[i].smsTemplateId &&
              !(formik.values.cadenceActions?.[i] as any).custom
            ) {
              const message = smsTemplateOptions.find(
                (sms) =>
                  String(sms.smsTemplateid) ===
                  String(formik.values.cadenceActions?.[i].smsTemplateId)
              );
              if (message) {
                formik.setFieldValue(
                  `cadenceActions.${i}.smsMessage`,
                  message.smsTemplateMessage
                );
              }
            }
          }
        }, [formik.values.cadenceActions, smsTemplateOptions]);
        return (
          <form
            className="form form-label-right"
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <TabContext value={String(tabIndex)}>
              <WizardModal
                className="cadenceModal"
                title={`Cadence ${tabNames[tabIndex].title}`}
                // disableSubmit={!formik.isValid}
                navigation={
                  <>
                    <Tab
                      label={props.id ? `Edit Cadence` : 'Add New Cadence'}
                      disabled={true}
                      className={clsx(classes.tab)}
                    />
                    <TabList
                      onChange={handleChange(formik)}
                      aria-label="Add New Cadence"
                      orientation="vertical"
                      variant="standard"
                      className={clsx(classes.tabs, {})}
                    >
                      {tabNames.map((tabItem, index) => (
                        <Tab
                          // name={tabItem.title}
                          key={index}
                          className={classes.tab}
                          label={
                            <TabLabel
                              index={index}
                              formik={formik}
                              tabToFields={tabToFields}
                              tabItem={tabItem}
                            />
                          }
                          value={String(index)}
                        />
                      ))}
                    </TabList>
                  </>
                }
                dialogActions={
                  <>
                    <Button
                      className={classes.button}
                      disabled={formik.isSubmitting}
                      variant="contained"
                      disableElevation={true}
                      color="inherit"
                      onClick={() => {
                        props.handleClose();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      className={clsx(classes.button)}
                      variant="contained"
                      disableElevation={true}
                      disabled={
                        formik.isSubmitting || !formik.dirty || !formik.isValid
                      }
                      onClick={(event: any) => {
                        // trigger show errors if errors exists
                        formik.handleSubmit(event);
                        // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                        if (
                          !formik.isValid && // if form invalid
                          !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        ) {
                          const errorTabIndex = getErrorTabIndex(
                            formik.errors,
                            tabToFields
                          );
                          /**
                           * switch to tab containing error
                           *
                           * since form validation is in flight and makes page rendering updates
                           * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                           * putting state changes in a separate tick will prevent coliding page rendering updates
                           *
                           * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                           */
                          setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        }
                      }}
                    >
                      Save
                    </Button>
                  </>
                }
                size="xl"
                // customWidth="1450px"
                show={props.show}
                onHide={props.handleClose}
                onSubmit={() => {
                  trackSaveCadence();
                  formik.handleSubmit();
                }}
                isLoading={
                  cadenceLoadable.state === 'loading' ||
                  smsTemplates.state === 'loading' ||
                  businessLoadable.state === 'loading' ||
                  disposition.state === 'loading'
                }
                isSubmitting={formik.isSubmitting}
                // disableSubmit={!formik.dirty}
              >
                <TabPanel value="0">
                  <Settings id={props.id} formik={formik} />
                </TabPanel>
                <TabPanel value="1">
                  {smsTemplates.state === 'hasValue' ? (
                    <CadenceActions
                      smsTemplateOptions={smsTemplateOptions}
                      hasWebhook={businessCached.current?.hasWebhook}
                      id={props.id}
                      formik={formik}
                    />
                  ) : (
                    'loading...'
                  )}
                </TabPanel>
                <TabPanel value="2">
                  <Dispositions
                    filteredDispoList={filteredDispoList}
                    dispoList={dispoList}
                    formik={formik}
                  />
                </TabPanel>
                <TabPanel value="3">
                  <HarassmentRules
                    formik={formik}
                    showBusinessHarasmentToggle={true}
                  />
                </TabPanel>
              </WizardModal>
            </TabContext>
          </form>
        );
      }}
    </Formik>
  );
}
